import React, { FunctionComponent, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Card,
  CardContent,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  Stack,
  SxProps,
  Typography,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  FilePresent as FilePresentIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import DropzoneS3Uploader from 'react-dropzone-s3-uploader';

import { IZoomMeetingRequest } from '../../../../../../store/V2/ZoomRequestsSlice';
import {
  defaultTextFieldProps,
  TPTextField,
} from '../../../../../../components/Inputs';
import DarkLoadingButton, {
  defaultButtonStyles,
} from '../../../../../../components/V2/Buttons';
import {
  createPenaltyAppealRequest,
  IPenaltyWaiver,
  TCreatePenaltyWaiver,
  IPenaltyWaiverFile,
} from '../../../../../../store/V2/PenaltyWaiverSlice';
import { scrubFileName } from '../../../../../../helper';
import BasicLoader from '../../../../../../components/Loaders/BasicLoader';
import { ApiPaths } from '../../../../../../api';

interface PenaltyAppealModalProps {
  open: boolean;
  meetingRequest: IZoomMeetingRequest;
  onClose: () => void;
  onSubmitted?: (
    result:
      | {
          penaltyWaiver: IPenaltyWaiver;
          zoomMeetingRequest: IZoomMeetingRequest;
        }
      | undefined
  ) => void;
}

type TState = {
  appealReasonText: string;
  isUploading: boolean;
  uploadPercentage: number;
  uploadingFileCount: number;
  files: Array<IPenaltyWaiverFile>;
};

const dropzoneStyles: React.CSSProperties = {
  height: '90px',
  borderRadius: '8px',
  border: '1px dashed #CBD5E1',
  color: '#94A3B8',
  backgroundColor: '#F1F5F9',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  textAlign: 'center',
};

const notDropzoneProps = [
  'onFinish',
  's3Url',
  'filename',
  'host',
  'upload',
  'isImage',
  'notDropzoneProps',
  'onDrop',
];

const initialState: TState = {
  appealReasonText: '',
  isUploading: false,
  uploadPercentage: 0,
  uploadingFileCount: 0,
  files: [],
};

const FileItemCard = ({
  name,
  onRemove,
}: {
  name: string;
  onRemove: () => void;
}) => {
  const cardContainerStyles: SxProps = {
    border: '1px solid #CBD5E1',
    borderRadius: '8px',
  };

  return (
    <Card elevation={0} sx={cardContainerStyles}>
      <CardContent style={{ padding: '5px 16px' }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center" gap="10px">
            <FilePresentIcon fontSize="small" color="info" />
            <Typography fontSize="14px" fontWeight={500} color="info.main">
              {name}
            </Typography>
          </Stack>
          <IconButton onClick={onRemove} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </CardContent>
    </Card>
  );
};

const PenaltyAppealModal: FunctionComponent<PenaltyAppealModalProps> = ({
  open,
  meetingRequest,
  onClose,
  onSubmitted,
}) => {
  const dispatch = useDispatch();

  // State
  const [state, setState] = useState<TState>({ ...initialState });
  const [isLoading, setIsLoading] = useState(false);

  const isSubmitDisabled = state.appealReasonText.trim().length < 3;

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const handleSubmit = async () => {
    setIsLoading(true);

    const apiData: TCreatePenaltyWaiver = {
      zoomRequestId: meetingRequest._id,
      reason: state.appealReasonText.trim(),
    };

    if (state.files.length) {
      apiData.files = state.files;
    }

    const res: any = await dispatch(createPenaltyAppealRequest(apiData));

    setIsLoading(false);

    if (onSubmitted) {
      onSubmitted(res && res.payload ? res.payload : undefined);
    }
  };

  const handleDropUpload = (files) => {
    setState((prevState) => ({
      ...prevState,
      isUploading: true,
      uploadingFileCount: prevState.uploadingFileCount + files.length,
    }));
  };

  const onUploadProgress = (percentage: number) => {
    setState((prevState) => ({
      ...prevState,
      uploadPercentage: percentage,
    }));
  };

  const handleFinishedUpload = async (file) => {
    const newFile = {
      name: file.objectName,
      url: `${process.env.REACT_APP_TUTOPIYA_S3_PENALTY_WAIVE_OFF_CONTENT_BUCKET_URL}/${file.objectName}`,
    };

    setState((prevState) => {
      const newState: TState = {
        ...prevState,
        files: [...prevState.files, newFile],
        uploadingFileCount:
          prevState.uploadingFileCount > 1
            ? prevState.uploadingFileCount - 1
            : 0,
        isUploading: prevState.uploadingFileCount > 1,
        uploadPercentage: 0,
      };

      return newState;
    });
  };

  const handeRemoveFileItem = (file) => {
    setState((prevState) => {
      const newFiles = [...prevState.files].filter(
        (newFile) => file.url !== newFile.url
      );

      return {
        ...prevState,
        files: newFiles,
      };
    });
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { borderRadius: '20px', maxWidth: '480px' },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: { xs: '15px', sm: '30px' },
          color: '#000000',
          fontWeight: 600,
          fontSize: '16px',
        }}
      >
        Penalty Appeal Form
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: { xs: '7px', sm: '22px' },
          top: { xs: '7px', sm: '22px' },
        }}
      >
        <CloseIcon sx={{ color: '#000000' }} />
      </IconButton>
      <DialogContent
        sx={{ p: { xs: '0 15px 15px 15px', sm: '0 30px 30px 30px' } }}
      >
        <div>
          <Grid container rowSpacing="16px">
            <Grid item xs={12}>
              <Typography fontSize="14px" fontWeight={500} color="#647488">
                Please fill out the form below to request a penalty waive off.
                Tutopiya team will review and get back within 10 working days.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Chip
                label={`Penalty Amount - ${
                  meetingRequest.tutorAmountCurrency || ''
                } ${(meetingRequest.penaltyAmount || 0).toFixed(2)}`}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <TPTextField
                  {...defaultTextFieldProps}
                  sx={{ backgroundColor: '#ffffff' }}
                  multiline
                  rows={2}
                  label="Reason for appeal *"
                  value={state.appealReasonText}
                  placeholder="Enter here..."
                  onChange={(event) =>
                    updateStateAttributes({
                      appealReasonText: event.target.value,
                    })
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Typography fontSize="14px" fontWeight={500} color="#647488">
                Upload Any Supporting Documents / Evidences To Justify Your
                Appeal.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <DropzoneS3Uploader
                maxSize={1024 * 1024 * 100}
                onFinish={handleFinishedUpload}
                onProgress={onUploadProgress}
                onDrop={handleDropUpload}
                style={dropzoneStyles}
                notDropzoneProps={notDropzoneProps}
                s3Url={
                  process.env
                    .REACT_APP_TUTOPIYA_S3_PENALTY_WAIVE_OFF_CONTENT_BUCKET_URL
                }
                upload={{
                  server: `${process.env.REACT_APP_TUTOPIYA_BASE_URL_V2}${ApiPaths.penaltyWaivers}`,
                  signingUrlQueryParams: {},
                  scrubFilename: scrubFileName,
                }}
              >
                {state.isUploading ? (
                  <>
                    <BasicLoader loaderProps={{ size: 25 }} />
                    <Typography fontSize="12px" marginTop="20px">
                      Uploading in Progress &nbsp;-&nbsp;
                      {state.uploadPercentage}% &nbsp; (
                      {state.uploadingFileCount} file
                      {state.uploadingFileCount > 1 ? 's' : null} Remaining)
                    </Typography>
                  </>
                ) : (
                  <>
                    <CloudUploadIcon sx={{ width: '38px', height: '24px' }} />
                    <Typography
                      fontSize="12px"
                      fontWeight={500}
                      color="#94A3B8"
                    >
                      Upload file
                    </Typography>
                  </>
                )}
              </DropzoneS3Uploader>
            </Grid>
            <Grid item xs={12}>
              <Collapse in={state.files.length > 0}>
                <Stack gap="8px">
                  {state.files.map((item, index) => (
                    <FileItemCard
                      key={`file-item-${index}`}
                      name={item.name}
                      onRemove={() => handeRemoveFileItem(item)}
                    />
                  ))}
                </Stack>
              </Collapse>
            </Grid>
          </Grid>
        </div>
      </DialogContent>
      <DialogActions
        sx={{ p: { xs: '0 15px 15px 15px', sm: '0 30px 30px 30px' } }}
      >
        <DarkLoadingButton
          disableElevation
          variant="contained"
          color="inherit"
          loading={isLoading}
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
          sx={{ ...defaultButtonStyles }}
        >
          Request
        </DarkLoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default PenaltyAppealModal;
